import { Injectable, Injector, InjectionToken, inject } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, Observable } from 'rxjs';

import { ToastComponent } from './toast.component';

export type ToastType = 'error' | 'info' | 'success' | 'warning' | 'debug';

export interface ToastData {
    type: ToastType;
    text: string;
    /** Auto-dismiss delay in ms for every type; 0 keeps the toast until dismissed. */
    duration?: number;
}

export class ToastRef {
    private afterClosedSubject = new Subject<void>();

    constructor(private readonly overlay: OverlayRef) { }

    close() {
        this.overlay.dispose();
        this.afterClosedSubject.next();
        this.afterClosedSubject.complete();
    }

    afterClosed(): Observable<void> {
        return this.afterClosedSubject.asObservable();
    }

    isVisible() {
        return this.overlay && this.overlay.overlayElement;
    }

    getOverlayElement() {
        return this.overlay?.overlayElement;
    }
}

export const TOAST_DATA = new InjectionToken<ToastData>('TOAST_DATA');
export const TOAST_REF = new InjectionToken<ToastRef>('TOAST_REF');

@Injectable({
    providedIn: 'root'
})
export class ToastService {
    private overlay = inject(Overlay);
    private injector = inject(Injector);

    private activeToasts: { ref: ToastRef, overlayRef: OverlayRef }[] = [];
    private readonly TOAST_HEIGHT = 70; // Height including margin
    /** Top centre of the editor, below its header. */
    private readonly TOAST_TOP = 56;
    private readonly TOAST_GAP = 10;

    show(data: ToastData): ToastRef {
        const positionStrategy = this.overlay.position()
            .global()
            .top(this.TOAST_TOP + 'px')
            .centerHorizontally();

        const overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: false,
            panelClass: 'toast-overlay-panel',
            width: '400px',
            maxWidth: 'calc(100vw - 32px)',
            height: 'auto'
        });

        const toastRef = new ToastRef(overlayRef);
        
        // Add to active toasts and update positions
        this.activeToasts.push({ ref: toastRef, overlayRef });
        this.updateAllPositions();

        toastRef.afterClosed().subscribe(() => {
            this.removeToast(toastRef);
        });

        const injector = this.createInjector(data, toastRef);
        const toastPortal = new ComponentPortal(ToastComponent, null, injector);

        overlayRef.attach(toastPortal);

        return toastRef;
    }

    success(text: string, duration?: number): ToastRef {
        return this.show({ type: 'success', text, duration });
    }

    error(text: string, duration?: number): ToastRef {
        return this.show({ type: 'error', text, duration });
    }

    warning(text: string, duration?: number): ToastRef {
        return this.show({ type: 'warning', text, duration });
    }

    info(text: string, duration?: number): ToastRef {
        return this.show({ type: 'info', text, duration });
    }

    debug(text: string, duration?: number): ToastRef {
        return this.show({ type: 'debug', text, duration });
    }

    private removeToast(toastRef: ToastRef) {
        const index = this.activeToasts.findIndex(toast => toast.ref === toastRef);
        if (index > -1) {
            this.activeToasts.splice(index, 1);
            this.updateAllPositions();
        }
    }

    private updateAllPositions() {
        this.activeToasts.forEach((toast, index) => {
            const newTop = this.TOAST_TOP + (index * (this.TOAST_HEIGHT + this.TOAST_GAP));
            const newPositionStrategy = this.overlay.position()
                .global()
                .top(newTop + 'px')
                .centerHorizontally();
                
            toast.overlayRef.updatePositionStrategy(newPositionStrategy);
        });
    }

    private getPositionStrategy() {
        // Calculate position based on currently active toasts
        const position = this.TOAST_TOP + (this.activeToasts.length * (this.TOAST_HEIGHT + this.TOAST_GAP));

        return this.overlay.position()
            .global()
            .top(position + 'px')
            .centerHorizontally();
    }

    private createInjector(data: ToastData, toastRef: ToastRef): Injector {
        return Injector.create({
            providers: [
                { provide: TOAST_DATA, useValue: data },
                { provide: TOAST_REF, useValue: toastRef }
            ],
            parent: this.injector
        });
    }
}